import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Descriptions,
  Alert,
  Spin,
  Typography,
  Collapse,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {
  useFIRsByWorkOrder,
  useCreateFIR,
  useApproveFIR,
  useRejectFIR,
  useDeleteFIR,
  useWorkOrderStages,
} from '../../hooks/useQCWorkflow';
import { FinalInspectionRequest, FIRLineItemResult } from '../../services/qc-workflow.service';
import { useUsers } from '@/hooks/useUsers';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';
import { isFirApproveQuantityValid } from '@/utils/firApproveRules';

const { TextArea } = Input;
const { Panel } = Collapse;
const { Text } = Typography;

interface FinalInspectionRequestProps {
  workOrderId: string;
}

const lineItemColumns: ColumnsType<FIRLineItemResult> = [
  {
    title: 'Line Item',
    key: 'line_item',
    render: (_, record) => record.workOrderLineItem?.item_description || record.work_order_line_item_id,
  },
  {
    title: 'Approved Qty',
    dataIndex: 'approved_quantity',
    key: 'approved_quantity',
    render: (qty: number) => Number(qty || 0).toFixed(2),
  },
  {
    title: 'Rejected Qty',
    dataIndex: 'rejected_quantity',
    key: 'rejected_quantity',
    render: (qty: number) => Number(qty || 0).toFixed(2),
  },
  {
    title: 'Rejection Reason',
    dataIndex: 'rejection_reason',
    key: 'rejection_reason',
    ellipsis: true,
  },
  {
    title: 'NCR',
    dataIndex: 'ncr_id',
    key: 'ncr_id',
    render: (ncrId: string) => (ncrId ? <Tag color="red">NCR Created</Tag> : '-'),
  },
  {
    title: 'Revised WO',
    dataIndex: 'revised_work_order_id',
    key: 'revised_work_order_id',
    render: (woId: string) => (woId ? <Tag color="blue">Revised WO Created</Tag> : '-'),
  },
];

const FinalInspectionRequestComponent: React.FC<FinalInspectionRequestProps> = ({ workOrderId }) => {
  const [form] = Form.useForm();
  const [firBeingRejected, setFirBeingRejected] = useState<string | null>(null);
  const [resultsFirId, setResultsFirId] = useState<string | null>(null);

  const { data: firs = [], isLoading, refetch } = useFIRsByWorkOrder(workOrderId);
  const { data: stages = [] } = useWorkOrderStages(workOrderId);
  const firWaived =
    stages.length > 0 &&
    stages.every((s) => s.status === 'Skipped' || !s.inspection_required);
  const { data: users = [] } = useUsers({ is_active: true });
  const userDisplayMap = useMemo(() => buildUserIdToLabelMap(users), [users]);
  const ru = (id?: string | null) => resolveUserDisplayFromMap(userDisplayMap, id, '—');
  const createMutation = useCreateFIR();
  const approveMutation = useApproveFIR();
  const rejectMutation = useRejectFIR();
  const deleteMutation = useDeleteFIR();

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync(workOrderId);
      refetch();
    } catch (error) {
      console.error('Error creating FIR:', error);
    }
  };

  const handleApprove = async (firId: string) => {
    try {
      await approveMutation.mutateAsync(firId);
      refetch();
    } catch (error) {
      console.error('Error approving FIR:', error);
    }
  };

  const handleReject = async (values: { rejectionReason: string }) => {
    if (!firBeingRejected) return;
    try {
      await rejectMutation.mutateAsync({
        id: firBeingRejected,
        rejectionReason: values.rejectionReason,
      });
      setFirBeingRejected(null);
      form.resetFields();
      refetch();
    } catch (error) {
      console.error('Error rejecting FIR:', error);
    }
  };

  const handleDelete = (fir: FinalInspectionRequest) => {
    Modal.confirm({
      title: 'Delete Final Inspection Report?',
      content: 'This will remove the FIR for this work order. You can create a new one afterward.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        await deleteMutation.mutateAsync({ id: fir.id, workOrderId });
        refetch();
      },
    });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Pending: 'default',
      'In Progress': 'processing',
      Approved: 'success',
      Rejected: 'error',
      Partial: 'warning',
    };
    return colors[status] || 'default';
  };

  const lineLabel = (fir: FinalInspectionRequest) => {
    const li = fir.workOrderLineItem;
    if (li?.item_description || li?.item_code) {
      const num = li.line_number != null ? `Line ${li.line_number}: ` : '';
      const code = li.item_code ? `[${li.item_code}] ` : '';
      const rev = li.revision ? ` (Rev ${li.revision})` : '';
      return `${num}${code}${li.item_description || ''}${rev}`.trim();
    }
    if (fir.work_order_line_item_id) return `Line item ${fir.work_order_line_item_id}`;
    return 'Work order (legacy)';
  };

  if (isLoading) {
    return <Spin />;
  }

  if (firs.length === 0) {
    return (
      <Card>
        {firWaived ? (
          <Alert
            message="Final Inspection not required"
            description="All operations on this work order have stage inspection turned off. Completing the work order moves it to Ready for Dispatch without creating a FIR."
            type="success"
            showIcon
          />
        ) : (
          <Alert
            message="No Final Inspection Request"
            description="Final Inspection Requests are normally created automatically when the work order is completed (one per line). Use Create if they are missing."
            type="info"
            showIcon
            action={
              <Button type="primary" onClick={handleCreate} loading={createMutation.isPending}>
                Create FIR
              </Button>
            }
          />
        )}
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Alert
        message="Per-line final inspection"
        description="Each work order line has its own FIR. Approve each line as needed. Inspection Release Notes (IRN) are created on the delivery note, not here."
        type="info"
        showIcon
      />

      <Collapse defaultActiveKey={firs[0]?.id ? [firs[0].id] : undefined}>
        {firs.map((fir) => (
          <Panel
            key={fir.id}
            header={
              <Space wrap>
                <span>{fir.fir_number}</span>
                <Text type="secondary">{lineLabel(fir)}</Text>
                <Tag color={getStatusColor(fir.status)}>{fir.status}</Tag>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Card
                styles={{ body: { paddingTop: 12 } }}
                extra={
                  <Space wrap>
                    {fir.status === 'In Progress' && (
                      <>
                        <Button
                          type="primary"
                          icon={<FileTextOutlined />}
                          onClick={() => setResultsFirId(fir.id)}
                        >
                          Record Results
                        </Button>
                        <Tooltip
                          title={
                            !isFirApproveQuantityValid(fir)
                              ? 'Approved + rejected must equal planned quantity (save line results first).'
                              : undefined
                          }
                        >
                          <span>
                            <Button
                              type="primary"
                              icon={<CheckCircleOutlined />}
                              onClick={() => handleApprove(fir.id)}
                              loading={approveMutation.isPending}
                              disabled={fir.rejected_quantity > 0 || !isFirApproveQuantityValid(fir)}
                            >
                              Approve
                            </Button>
                          </span>
                        </Tooltip>
                        <Button
                          danger
                          icon={<CloseCircleOutlined />}
                          onClick={() => setFirBeingRejected(fir.id)}
                          disabled={fir.rejected_quantity === 0}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    <Button danger onClick={() => handleDelete(fir)} loading={deleteMutation.isPending}>
                      Delete FIR
                    </Button>
                  </Space>
                }
              >
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="FIR Number">{fir.fir_number}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(fir.status)}>{fir.status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Requested Date">
                    {(fir.requested_date || fir.inspection_date || fir.created_at)
                      ? new Date(fir.requested_date || fir.inspection_date || fir.created_at).toLocaleDateString()
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Requested By">
                    {fir.requester?.name || fir.inspector_name || ru(fir.requested_by)}
                  </Descriptions.Item>
                  {fir.inspector_id && (
                    <>
                      <Descriptions.Item label="Inspector">
                        {fir.inspector?.name || ru(fir.inspector_id)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Inspection Date">
                        {fir.inspection_date ? new Date(fir.inspection_date).toLocaleDateString() : '-'}
                      </Descriptions.Item>
                    </>
                  )}
                  <Descriptions.Item label="Approved Quantity">
                    {Number(fir.approved_quantity || 0).toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rejected Quantity">
                    {Number(fir.rejected_quantity || 0) > 0 ? (
                      <Text type="danger">{Number(fir.rejected_quantity || 0).toFixed(2)}</Text>
                    ) : (
                      Number(fir.rejected_quantity || 0).toFixed(2)
                    )}
                  </Descriptions.Item>
                  {fir.approved_date && (
                    <>
                      <Descriptions.Item label="Approved Date">
                        {new Date(fir.approved_date).toLocaleDateString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Approved By">
                        {fir.approver?.name || ru(fir.approved_by)}
                      </Descriptions.Item>
                    </>
                  )}
                  {fir.rejection_reason && (
                    <Descriptions.Item label="Rejection Reason" span={2}>
                      {fir.rejection_reason}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              {fir.stage_summary && (
                <Card title="Stage summary" size="small">
                  <Collapse size="small">
                    {fir.stage_summary.map((stage: any, index: number) => (
                      <Panel
                        key={stage.stage_id || index}
                        header={`${stage.stage_name} (${stage.stage_code})`}
                      >
                        <Descriptions column={2} size="small">
                          <Descriptions.Item label="Status">
                            <Tag>{stage.status}</Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Sequence">{stage.sequence_number}</Descriptions.Item>
                          {stage.started_date && (
                            <Descriptions.Item label="Started">
                              {new Date(stage.started_date).toLocaleString(undefined, {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })}
                            </Descriptions.Item>
                          )}
                          {stage.completed_date && (
                            <Descriptions.Item label="Completed">
                              {new Date(stage.completed_date).toLocaleString(undefined, {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })}
                            </Descriptions.Item>
                          )}
                          {stage.inspection && (
                            <>
                              <Descriptions.Item label="Inspection Number">
                                {stage.inspection.inspection_number}
                              </Descriptions.Item>
                              <Descriptions.Item label="Inspection Result">
                                <Tag color={stage.inspection.result === 'Pass' ? 'success' : 'error'}>
                                  {stage.inspection.result}
                                </Tag>
                              </Descriptions.Item>
                            </>
                          )}
                        </Descriptions>
                      </Panel>
                    ))}
                  </Collapse>
                </Card>
              )}

              {fir.lineItemResults && fir.lineItemResults.length > 0 && (
                <Card title="Line item results" size="small">
                  <Table
                    columns={lineItemColumns}
                    dataSource={fir.lineItemResults}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              )}

              {fir.status === 'Approved' && (
                <Alert
                  message="FIR approved"
                  description="This line’s approved quantity can ship on a delivery note once you create and approve an IRN on that note."
                  type="success"
                  showIcon
                />
              )}

              {fir.status === 'Rejected' && (
                <Alert
                  message="FIR rejected"
                  description="NCR records and revised work orders have been created for rejected quantities where applicable."
                  type="error"
                  showIcon
                />
              )}

              {fir.status === 'Partial' && (
                <Alert
                  message="Partial approval"
                  description="Some quantities were approved while others were rejected. Revised work orders may have been created for rejected quantities."
                  type="warning"
                  showIcon
                />
              )}
            </Space>
          </Panel>
        ))}
      </Collapse>

      <Modal
        title="Reject FIR"
        open={firBeingRejected != null}
        onOk={() => form.submit()}
        onCancel={() => {
          setFirBeingRejected(null);
          form.resetFields();
        }}
        confirmLoading={rejectMutation.isPending}
      >
        <Form form={form} layout="vertical" onFinish={handleReject}>
          <Alert
            message="Rejecting FIR will create NCR records and revised work orders for rejected quantities."
            type="warning"
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            name="rejectionReason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please enter rejection reason' }]}
          >
            <TextArea rows={4} placeholder="Enter reason for rejection" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Record results"
        open={resultsFirId != null}
        onOk={() => setResultsFirId(null)}
        onCancel={() => setResultsFirId(null)}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Alert
          type="info"
          showIcon
          message="Line results"
          description="Use the quality API or future UI wiring to post approved/rejected quantities for this FIR."
        />
      </Modal>
    </Space>
  );
};

export default FinalInspectionRequestComponent;
